import type { InstructionActionValue } from "@schemas/instruction";
import type { AbiParameter, AbiParametersToPrimitiveTypes } from "abitype";
import type { Hex } from "viem";

import { decodeAbiParameters, isHex, size, slice } from "viem";

import { buildInstructionActionArgumentsAbiMap } from "@contracts/artifacts/abi/build-instruction-action-arguments-abi";

type AbiParameterMapping<TKey extends string> = Readonly<
  Record<TKey, readonly AbiParameter[]>
>;

interface DecodedAbiResult<TAbiParameters extends readonly AbiParameter[]> {
  readonly values: AbiParametersToPrimitiveTypes<TAbiParameters>;
  readonly named: {
    readonly [K in TAbiParameters[number] as K extends { name: infer N }
      ? N extends string
        ? N
        : never
      : never]: K extends { name: string }
      ? AbiParametersToPrimitiveTypes<[K]>[0]
      : unknown;
  };
}

function validateHexData(data: Hex, context: string): void {
  if (!isHex(data)) {
    throw new Error(
      `Invalid hex data format in ${String(context)}: ${String(data)}`,
    );
  }

  if (size(data) === 0) {
    throw new Error(`Empty hex data provided for ${String(context)}`);
  }
}

function extractParameterNames(parameters: readonly AbiParameter[]): string[] {
  return parameters.map(
    (param, index) => param.name || `param_${String(index)}`,
  );
}

function createNamedResult<TAbiParameters extends readonly AbiParameter[]>(
  parameters: TAbiParameters,
  decodedValues: AbiParametersToPrimitiveTypes<TAbiParameters>,
): DecodedAbiResult<TAbiParameters>["named"] {
  const names = extractParameterNames(parameters);

  return Object.fromEntries(
    names.map((name, index) => [name, decodedValues[index]]),
  ) as DecodedAbiResult<TAbiParameters>["named"];
}

// decodes abi-encoded data for a specific parameter type
export function decodeAbiData<
  TKey extends string,
  TMapping extends AbiParameterMapping<TKey>,
>(
  mapping: TMapping,
  type: TKey,
  data: Hex,
  options?: {
    offset?: number;
    size?: number;
  },
): DecodedAbiResult<TMapping[TKey]> {
  // validate input parameters
  if (!(type in mapping)) {
    const availableTypes = Object.keys(mapping).join(", ");
    throw new Error(
      `Unsupported ABI type: "${type}". Available types: ${availableTypes}`,
    );
  }

  const parameters = mapping[type];
  if (!parameters || parameters.length === 0) {
    throw new Error(`No ABI parameters defined for type: ${type}`);
  }

  // validate and optionally slice hex data
  validateHexData(data, `type "${type}"`);

  let dataToDecodes = data;
  if (options?.offset !== undefined || options?.size !== undefined) {
    const start = options.offset || 0;
    const end = options.size ? start + options.size : undefined;
    dataToDecodes = slice(data, start, end);
    validateHexData(dataToDecodes, `sliced data for type "${type}"`);
  }

  try {
    // decode using viem's decodeAbiParameters
    const decodedValues = decodeAbiParameters(
      parameters,
      dataToDecodes,
    ) as AbiParametersToPrimitiveTypes<TMapping[TKey]>;

    // create named access object
    const namedResult = createNamedResult(parameters, decodedValues);

    return {
      values: decodedValues,
      named: namedResult,
    } as DecodedAbiResult<TMapping[TKey]>;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(
      `Failed to decode ABI data for type "${type}": ${errorMessage}`,
    );
  }
}

// specialized decoder for instruction action arguments
export function decodeBuildInstructionActionArguments(
  action: InstructionActionValue,
  data: Hex,
): DecodedAbiResult<
  (typeof buildInstructionActionArgumentsAbiMap)[typeof action]
>["named"] {
  const result = decodeAbiData(
    buildInstructionActionArgumentsAbiMap,
    action,
    data,
  );
  return result.named;
}

export type DecodedInstructionArguments<T extends InstructionActionValue> =
  DecodedAbiResult<(typeof buildInstructionActionArgumentsAbiMap)[T]>["named"];

// type guard to check if decoded data has expected properties
export function isValidDecodedResult<T extends readonly AbiParameter[]>(
  result: unknown,
  expectedParameters: T,
): result is DecodedAbiResult<T>["named"] {
  if (!result || typeof result !== "object") {
    return false;
  }

  const names = extractParameterNames(expectedParameters);
  return names.every((name) => name in (result as Record<string, unknown>));
}

export { isHex, size, slice, decodeAbiParameters } from "viem";

export type { AbiParameter, AbiParametersToPrimitiveTypes } from "abitype";
